import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import './AdminOrderList.css';
import { getAllOrders, archiveOrder, updateOrderStatus, cancelExpiredOrders, ORDER_STATUS } from '../../firebase/orders';

const ENTRIES_OPTIONS = [5, 10, 25, 50];

const STATUS_CONFIG = {
    'pending':                           { bg:'#fff8e1', text:'#b07c00', label:'Pending Approval' },
    [ORDER_STATUS.PENDING_APPROVAL]:     { bg:'#fff8e1', text:'#b07c00', label:'Pending Approval' },
    [ORDER_STATUS.APPROVED]:             { bg:'#e3f0ff', text:'#1a5faa', label:'Approved' },
    [ORDER_STATUS.AWAITING_PAYMENT]:     { bg:'#f3e5f5', text:'#7b1fa2', label:'Awaiting Payment' },
    [ORDER_STATUS.IN_PRODUCTION]:        { bg:'#e6f4ea', text:'#2d7a3a', label:'In Production' },
    'in production':                     { bg:'#e6f4ea', text:'#2d7a3a', label:'In Production' },
    'processing':                        { bg:'#e6f4ea', text:'#2d7a3a', label:'In Production' },
    [ORDER_STATUS.TO_BE_DELIVERED]:      { bg:'#fff3e0', text:'#e65100', label:'To Be Delivered' },
    [ORDER_STATUS.DELIVERED]:            { bg:'#e8f5e9', text:'#1b5e20', label:'Delivered' },
    'completed':                         { bg:'#e8f5e9', text:'#1b5e20', label:'Delivered' },
    [ORDER_STATUS.CANCELLED]:            { bg:'#fdecea', text:'#b71c1c', label:'Cancelled' },
};

const STATUS_FILTER_TABS = [
    { key:'all',                          label:'All' },
    { key:ORDER_STATUS.PENDING_APPROVAL,  label:'Pending Approval', match:['pending', ORDER_STATUS.PENDING_APPROVAL] },
    { key:ORDER_STATUS.APPROVED,          label:'Approved',          match:[ORDER_STATUS.APPROVED] },
    { key:ORDER_STATUS.AWAITING_PAYMENT,  label:'Awaiting Payment',  match:[ORDER_STATUS.AWAITING_PAYMENT] },
    { key:ORDER_STATUS.IN_PRODUCTION,     label:'In Production',     match:[ORDER_STATUS.IN_PRODUCTION, 'in production', 'processing'] },
    { key:ORDER_STATUS.TO_BE_DELIVERED,   label:'To Be Delivered',   match:[ORDER_STATUS.TO_BE_DELIVERED] },
    { key:ORDER_STATUS.DELIVERED,         label:'Delivered',         match:[ORDER_STATUS.DELIVERED, 'completed'] },
    { key:ORDER_STATUS.CANCELLED,         label:'Cancelled',         match:[ORDER_STATUS.CANCELLED] },
];

const CANCELLABLE_STATUSES = ['pending', ORDER_STATUS.PENDING_APPROVAL, ORDER_STATUS.APPROVED];
const DELETABLE_STATUSES   = [...CANCELLABLE_STATUSES, ORDER_STATUS.CANCELLED, ORDER_STATUS.DELIVERED];

const NO_COLOR_SUBCATEGORIES = ['Polo Shirt', 'Jersey'];

const IMPRINT_RECT = {
    'Full Front':150,'Medium Front':120,'Center Chest':50,'Across Chest':80,
    'Left Chest':50,'Right Chest':50,'Left Vertical':120,'Right Vertical':120,
    'Bottom Left':50,'Bottom Right':50,'Full Back':150,'Medium Back':120,
    'Locker Center Patch':50,'Across Shoulder':80,'Left Sleeve':40,'Right Sleeve':40,
};
const IMPRINT_CIRC = {
    'Full Front':120,'Medium Front':80,'Center Chest':50,'Across Chest':80,
    'Left Chest':40,'Right Chest':40,'Left Vertical':100,'Right Vertical':100,
    'Bottom Left':50,'Bottom Right':50,'Full Back':120,'Medium Back':80,
    'Locker Center Patch':50,'Back Center':80,'Left Sleeve':40,'Right Sleeve':40,
};

const SHIRT_SIZE_PRICES = {
    'Active Life White T-Shirt':        { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:135, xxxxl:null, xxxxxl:140 },
    'Active Life Colored T-Shirt':      { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:140, xxxxl:null, xxxxxl:150 },
    'Blue Corner White T-Shirt':        { xs:150, small:150, medium:150, large:160, xl:165, xxl:170, xxxl:175, xxxxl:180, xxxxxl:200 },
    'Blue Corner Colored T-Shirt':      { xs:180, small:180, medium:180, large:195, xl:195, xxl:205, xxxl:210, xxxxl:220, xxxxxl:235 },
    'Yalex White T-Shirt':              { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:135, xxxxl:null, xxxxxl:140 },
    'Yalex Colored T-Shirt':            { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:140, xxxxl:null, xxxxxl:150 },
    'Drift White T-Shirt':              { xs:85,  small:85,  medium:85,  large:85,  xl:90,  xxl:95,  xxxl:null, xxxxl:null, xxxxxl:null },
    'Drift Colored T-Shirt':            { xs:95,  small:95,  medium:95,  large:95,  xl:105, xxl:115, xxxl:null, xxxxl:null, xxxxxl:null },
    'Blackhorse White T-Shirt':         { xs:120, small:120, medium:120, large:120, xl:125, xxl:130, xxxl:null, xxxxl:null, xxxxxl:null },
    'Blackhorse Colored T-Shirt':       { xs:125, small:125, medium:125, large:125, xl:130, xxl:135, xxxl:null, xxxxl:null, xxxxxl:null },
    'Pro Club Inspire White T-Shirt':   { xs:null, small:200, medium:200, large:200, xl:220, xxl:230, xxxl:null, xxxxl:null, xxxxxl:null },
    'Pro Club Inspire Colored T-Shirt': { xs:null, small:250, medium:250, large:250, xl:260, xxl:270, xxxl:null, xxxxl:null, xxxxxl:null },
};

function getCaseInsensitivePrice(sizePrices, sizeKey) {
    if (!sizePrices || !sizeKey) return 0;
    if (sizePrices[sizeKey] != null) return Number(sizePrices[sizeKey]) || 0;
    const normalizedTarget = String(sizeKey).trim().toLowerCase();
    const matchedKey = Object.keys(sizePrices).find(
        (key) => String(key).trim().toLowerCase() === normalizedTarget
    );
    return matchedKey ? (Number(sizePrices[matchedKey]) || 0) : 0;
}

function isGenericProduct(item) {
    const sub = item?.subcategory;
    if (!sub) return false;
    return sub !== 'T-Shirts' && sub !== 'Jersey' && sub !== 'Polo Shirt';
}

function calcItemTotal(item) {
    if (isGenericProduct(item)) {
        return (Number(item.quantity) || 0) * (Number(item.price) || 0);
    }
    const quantities = item.quantities || {};
    let totalQty   = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
    if (totalQty === 0) totalQty = Number(item.quantity) || 0;
    if (totalQty === 0) return 0;

    if (NO_COLOR_SUBCATEGORIES.includes(item.subcategory)) {
        const sizePrices = item.sizePrices || {};
        if (Object.keys(sizePrices).length > 0 && Object.keys(quantities).length > 0) {
            const calculatedTotal = Object.entries(quantities).reduce(
                (s, [size, q]) => s + (q || 0) * getCaseInsensitivePrice(sizePrices, size), 0
            );
            if (calculatedTotal > 0) return calculatedTotal;
        }
        if (Number(item.itemTotal) > 0) return Number(item.itemTotal);
        return totalQty * (Number(item.price) || 0);
    }

    const sizePrices     = item.sizePrices && Object.keys(item.sizePrices).length > 0
        ? item.sizePrices
        : (SHIRT_SIZE_PRICES[item.type] || {});
    const colorSurcharge = Number(item.colorSurcharge || 0);
    
    let shirtSubtotal = 0;
    if (Object.keys(quantities).length > 0) {
        shirtSubtotal = Object.entries(quantities).reduce(
            (s, [size, q]) => s + (q || 0) * (getCaseInsensitivePrice(sizePrices, size) + colorSurcharge), 0
        );
    } else {
        shirtSubtotal = totalQty * ((Number(item.price) || 0) + colorSurcharge);
    }

    const shape        = (item.designShape || '').toLowerCase() === 'circle' ? 'circle' : 'rectangle';
    const table        = shape === 'circle' ? IMPRINT_CIRC : IMPRINT_RECT;
    const locs         = (item.printLocation || '').split(',').map(s => s.trim()).filter(Boolean);
    const prices       = locs.map(l => table[l] || 0).sort((a, b) => b - a);
    const imprintPerPc = prices.reduce((s, p, i) => s + (i === 0 ? p : Math.round(p * 0.5)), 0);

    return shirtSubtotal + imprintPerPc * totalQty;
}

function getOrderGrandTotal(order) {
    const items = order.orderItems || [];
    if (items.length > 0) {
        const subtotal       = items.reduce((s, item) => s + calcItemTotal(item), 0);
        const deliveryFee    = Number(order.deliveryFee    || 0);
        const discountAmount = Number(order.discountAmount || 0);
        return Math.max(0, subtotal + deliveryFee - discountAmount);
    }
    if (order.total !== undefined && order.total !== null) return order.total;
    return Math.max(0, (order.subtotal || 0) - (order.discountAmount || 0));
}

function formatDate(value) {
    if (!value) return '—';
    const d = value?.toDate ? value.toDate() : new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('en-PH', { month:'short', day:'numeric', year:'numeric', timeZone:'Asia/Manila' });
}

// ── Extract delivered date from statusHistory ─────────────────────────────────
function getDeliveredDate(order) {
    const history = order.statusHistory || [];
    const entry   = [...history].reverse().find(h => h.status === ORDER_STATUS.DELIVERED || h.status === 'completed');
    if (!entry) return order.updatedAt ? formatDate(order.updatedAt) : '—';
    // timestamp is stored as ISO string by makeStatusEntry
    const d = new Date(entry.timestamp);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-PH', { month:'short', day:'numeric', year:'numeric', timeZone:'Asia/Manila' });
}

// ── Delivered-only Excel export ───────────────────────────────────────────────
function exportDeliveredExcel(orders, onError) {
    const deliveredOrders = orders.filter(
        o => o.status === ORDER_STATUS.DELIVERED || o.status === 'completed'
    );

    if (deliveredOrders.length === 0) {
        if (onError) onError('No delivered orders to export.');
        return;
    }

    const rows = [];

    for (const order of deliveredOrders) {
        const deliveredDate  = getDeliveredDate(order);
        const discountAmount = Number(order.discountAmount || 0);
        const orderItems     = order.orderItems || [];
        const grandTotal     = getOrderGrandTotal(order);

        // Distribute discount proportionally across items
        const subtotal = orderItems.length > 0
            ? orderItems.reduce((s, item) => s + calcItemTotal(item), 0)
            : grandTotal;

        if (orderItems.length === 0) {
            // Order has no item breakdown — emit one summary row
            rows.push({
                'Order ID':       order.orderId       || '—',
                'Customer':       order.customerName  || '—',
                'Item Name':      '(no item breakdown)',
                'Item Price (₱)': grandTotal,
                'Discount (₱)':   discountAmount,
                'Final Amount (₱)': Math.max(0, grandTotal - discountAmount),
                'Date Delivered': deliveredDate,
            });
            continue;
        }

        orderItems.forEach((item, idx) => {
            const itemPrice    = calcItemTotal(item);
            // Distribute discount proportionally: item share = discount × (itemPrice / subtotal)
            const itemDiscount = subtotal > 0
                ? Math.round((discountAmount * itemPrice / subtotal) * 100) / 100
                : 0;
            const itemFinal    = Math.max(0, itemPrice - itemDiscount);

            // Build a readable item name
            const nameParts = [item.name || item.type || 'Item'];
            if (item.color)    nameParts.push(item.color);
            if (item.subcategory && item.subcategory !== item.name) nameParts.push(`(${item.subcategory})`);

            // Quantity summary e.g. "S×2, M×3"
            const qtySummary = Object.entries(item.quantities || {})
                .filter(([, q]) => q > 0)
                .map(([size, q]) => `${size.toUpperCase()}×${q}`)
                .join(', ');

            rows.push({
                'Order ID':         idx === 0 ? (order.orderId || '—') : '',  // only on first item row
                'Customer':         idx === 0 ? (order.customerName || '—') : '',
                'Item Name':        nameParts.join(' — '),
                'Quantities':       qtySummary || '—',
                'Item Price (₱)':   itemPrice,
                'Discount (₱)':     itemDiscount > 0 ? itemDiscount : 0,
                'Final Amount (₱)': itemFinal,
                'Date Delivered':   idx === 0 ? deliveredDate : '',  // only on first item row
            });
        });

        // Blank separator row between orders
        rows.push({
            'Order ID': '', 'Customer': '', 'Item Name': '', 'Quantities': '',
            'Item Price (₱)': '', 'Discount (₱)': '', 'Final Amount (₱)': '', 'Date Delivered': '',
        });
    }

    // Build workbook
    const ws = XLSX.utils.json_to_sheet(rows);

    // Column widths
    ws['!cols'] = [
        { wch: 22 }, // Order ID
        { wch: 22 }, // Customer
        { wch: 34 }, // Item Name
        { wch: 20 }, // Quantities
        { wch: 16 }, // Item Price
        { wch: 14 }, // Discount
        { wch: 18 }, // Final Amount
        { wch: 18 }, // Date Delivered
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Delivered Orders');

    const filename = `delivered-orders-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
}

export default function OrderList() {
    const navigate = useNavigate();
    const [search,       setSearch]       = useState('');
    const [entries,      setEntries]      = useState(10);
    const [page,         setPage]         = useState(1);
    const [orders,            setOrders]            = useState([]);
    const [loading,           setLoading]           = useState(true);
    const [error,             setError]             = useState(null);
    const [actionError,       setActionError]       = useState(null);
    const [successMessage,    setSuccessMessage]    = useState(null);
    const [deleteDialog,      setDeleteDialog]      = useState(null);
    const [bulkDeleteDialog,  setBulkDeleteDialog]  = useState(false);
    const [selectedOrderIds,  setSelectedOrderIds]  = useState({});
    const [statusFilter,      setStatusFilter]      = useState('all');
    const selectAllCheckboxRef = useRef(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true); setError(null);
            await cancelExpiredOrders();
            const { orders: fetched, error: fetchError } = await getAllOrders();
            if (fetchError) setError(fetchError);
            else setOrders(fetched);
            setLoading(false);
        };
        load();
    }, []);

    const filtered = orders.filter(o => {
        const tab = STATUS_FILTER_TABS.find(t => t.key === statusFilter);
        const matchesStatus = statusFilter === 'all' || (tab?.match || [statusFilter]).includes(o.status);
        const q = search.toLowerCase();
        const matchesSearch = !q ||
            o.orderId?.toLowerCase().includes(q) ||
            o.customerName?.toLowerCase().includes(q) ||
            o.status?.toLowerCase().includes(q);
        return matchesStatus && matchesSearch;
    });

    const totalPages = Math.max(1, Math.ceil(filtered.length / entries));
    const paginated  = filtered.slice((page - 1) * entries, page * entries);

    useEffect(() => { setPage(1); }, [search, entries, statusFilter]);
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const countByTab = (tab) => {
        if (tab.key === 'all') return orders.length;
        const matchSet = new Set(tab.match || [tab.key]);
        return orders.filter(o => matchSet.has(o.status)).length;
    };

    const isOrderSelectable = (order) => DELETABLE_STATUSES.includes(order.status);
    const visibleSelectableOrders = paginated.filter(isOrderSelectable);
    const visibleSelectableOrderIds = visibleSelectableOrders.map((order) => order.firestoreId);
    const selectAllVisibleDisabled = visibleSelectableOrderIds.length === 0;
    const areAllVisibleSelected = !selectAllVisibleDisabled && visibleSelectableOrderIds.every((id) => Boolean(selectedOrderIds[id]));
    const selectedOrders = orders.filter((order) => selectedOrderIds[order.firestoreId] && isOrderSelectable(order));
    const selectedCount = selectedOrders.length;

    useEffect(() => {
        if (selectAllCheckboxRef.current) {
            selectAllCheckboxRef.current.indeterminate = !areAllVisibleSelected
                && visibleSelectableOrderIds.some((id) => Boolean(selectedOrderIds[id]));
        }
    }, [areAllVisibleSelected, selectedOrderIds, visibleSelectableOrderIds]);

    const toggleOrderSelection = (firestoreId) => {
        setActionError(null);
        setSuccessMessage(null);
        setSelectedOrderIds((prev) => {
            const next = { ...prev };
            if (next[firestoreId]) delete next[firestoreId];
            else next[firestoreId] = true;
            return next;
        });
    };

    const toggleSelectAllVisible = () => {
        if (selectAllVisibleDisabled) return;
        setActionError(null);
        setSuccessMessage(null);
        setSelectedOrderIds((prev) => {
            const next = { ...prev };
            const allSelected = areAllVisibleSelected;
            visibleSelectableOrderIds.forEach((id) => {
                if (allSelected) delete next[id];
                else next[id] = true;
            });
            return next;
        });
    };

    const handleBulkDelete = () => {
        if (selectedCount === 0) return;
        setActionError(null);
        setSuccessMessage(null);
        setBulkDeleteDialog(true);
    };

    const handleConfirmBulkDelete = async () => {
        setBulkDeleteDialog(false);
        const itemsToDelete = selectedOrders;
        if (itemsToDelete.length === 0) {
            setActionError('No orders selected for deletion.');
            return;
        }
        setActionError(null);
        setSuccessMessage(null);

        const results = await Promise.allSettled(itemsToDelete.map(async (order) => {
            if (order.status === ORDER_STATUS.CANCELLED || order.status === ORDER_STATUS.DELIVERED) {
                const result = await archiveOrder(order.firestoreId);
                return { order, ...result };
            }
            const orderTotal = order.total !== undefined && order.total !== null
                ? order.total
                : getOrderGrandTotal(order);
            const result = await updateOrderStatus(order.firestoreId, ORDER_STATUS.CANCELLED, {
                userId:             order.userId,
                customerName:       order.customerName,
                customerEmail:      order.customerEmail,
                orderId:            order.orderId,
                orderTotal,
                cancellationReason: 'Cancelled by admin via bulk delete',
                note:               'Cancelled by admin via order list',
            });
            return { order, ...result };
        }));

        const processedIds = new Set();
        const failed = [];

        results.forEach((result, index) => {
            const order = itemsToDelete[index];
            if (result.status === 'fulfilled' && result.value.success) {
                processedIds.add(order.firestoreId);
            } else {
                const errorMessage = result.status === 'fulfilled'
                    ? result.value.error || 'Unknown error'
                    : String(result.reason || 'Unknown error');
                failed.push({ order, error: errorMessage });
            }
        });

        setOrders((prev) => prev.filter((order) => !processedIds.has(order.firestoreId)));

        setSelectedOrderIds((prev) => {
            const next = { ...prev };
            processedIds.forEach((id) => delete next[id]);
            return next;
        });

        const processedCount = processedIds.size;
        if (processedCount > 0) {
            setSuccessMessage(`Processed ${processedCount} selected order${processedCount === 1 ? '' : 's'} successfully.`);
        }
        if (failed.length > 0) {
            setActionError(`Failed to process ${failed.length} selected order${failed.length === 1 ? '' : 's'}: ${failed.map((item) => item.order.orderId).join(', ')}.`);
        }
    };

    const handleDelete = async () => {
        if (!deleteDialog) return;
        const { firestoreId, orderId, status, userId, customerName, customerEmail, total } = deleteDialog;
        setDeleteDialog(null);

        if (status === ORDER_STATUS.CANCELLED || status === ORDER_STATUS.DELIVERED) {
            const { success, error } = await archiveOrder(firestoreId);
            if (success) setOrders(prev => prev.filter(o => o.firestoreId !== firestoreId));
            else setActionError(error || 'Failed to archive order.');
            return;
        }

        const { success, error } = await updateOrderStatus(firestoreId, ORDER_STATUS.CANCELLED, {
            userId, customerName, customerEmail, orderId,
            orderTotal:         total || 0,
            cancellationReason: 'Cancelled by admin.',
            note:               'Cancelled by admin via order list',
        });

        if (success) {
            setOrders(prev => prev.map(o =>
                o.firestoreId === firestoreId
                    ? { ...o, status:ORDER_STATUS.CANCELLED, cancellationReason:'Cancelled by admin.' }
                    : o
            ));
        } else {
            setActionError(error || 'Failed to cancel order.');
        }
    };

    // ── CSV export (all/filtered orders) ─────────────────────────────────────
    const handleExport = () => {
        const header = 'Order ID,Customer Name,Items,Total Amount,Order Date,Status\n';
        const rows   = filtered.map(o =>
            `${o.orderId},"${o.customerName}",${o.items},${getOrderGrandTotal(o).toFixed(2)},${o.date},${o.status}`
        ).join('\n');
        const blob = new Blob([header + rows], { type:'text/csv' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.download = `orders-${new Date().toISOString().split('T')[0]}.csv`;
        a.click(); URL.revokeObjectURL(url);
    };

    if (loading) return <div className="aop-wrapper"><div className="aop-loading">Loading orders…</div></div>;
    if (error)   return <div className="aop-wrapper"><div className="aop-error">Error: {error}</div></div>;

    return (
        <div className="aop-wrapper">
            {deleteDialog && (
                <div className="aop-dialog-overlay" onClick={() => setDeleteDialog(null)}>
                    <div className="aop-dialog" onClick={e => e.stopPropagation()}>
                        <h3 className="aop-dialog-title">
                            {deleteDialog.status === ORDER_STATUS.CANCELLED || deleteDialog.status === ORDER_STATUS.DELIVERED
                                ? 'Move to Order Archive' : 'Cancel Order'}
                        </h3>
                        <p className="aop-dialog-message">
                            {deleteDialog.status === ORDER_STATUS.CANCELLED || deleteDialog.status === ORDER_STATUS.DELIVERED
                                ? <>Move order <strong>{deleteDialog.orderId}</strong> to Order Archive? It will be removed from the order list.</>
                                : <>Cancel order <strong>{deleteDialog.orderId}</strong>? This cannot be undone.</>
                            }
                        </p>
                        {deleteDialog.status === ORDER_STATUS.APPROVED && (
                            <p className="aop-dialog-warning">⚠️ Are you sure you want to cancel? This order has already been approved.</p>
                        )}
                        <div className="aop-dialog-actions">
                            <button className="aop-dialog-btn aop-dialog-btn--secondary" onClick={() => setDeleteDialog(null)}>Cancel</button>
                            <button className="aop-dialog-btn aop-dialog-btn--danger" onClick={handleDelete}>
                                {deleteDialog.status === ORDER_STATUS.CANCELLED || deleteDialog.status === ORDER_STATUS.DELIVERED ? 'Move' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Top bar */}
            <div className="aop-topbar">
                <div className="aop-topbar-left">
                    <label className="aop-entries-label">
                        Show
                        <select className="aop-entries-select" value={entries} onChange={e => setEntries(Number(e.target.value))}>
                            {ENTRIES_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                        entries
                    </label>
                </div>
                <div className="aop-search-box">
                    <input type="text" className="aop-search-input"
                        placeholder="Search by order ID, customer name or status…"
                        value={search} onChange={e => setSearch(e.target.value)} />
                    <span className="aop-search-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                    </span>
                </div>
                <div className="aop-topbar-right">
                    {/* ── Delivered Excel export ── */}
                    <button className="aop-export-btn aop-export-btn--excel" onClick={() => exportDeliveredExcel(orders, setActionError)}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="12" y1="18" x2="12" y2="12"/>
                            <line x1="9" y1="15" x2="15" y2="15"/>
                        </svg>
                        Delivered Report
                    </button>
                    {/* ── CSV export ── */}
                    <button className="aop-export-btn" onClick={handleExport}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Status filter tabs */}
            <div className="aop-filter-tabs">
                {STATUS_FILTER_TABS.map(tab => {
                    const count = countByTab(tab);
                    return (
                        <button key={tab.key} className={`aop-filter-tab ${statusFilter === tab.key ? 'is-active' : ''}`}
                            onClick={() => setStatusFilter(tab.key)}>
                            {tab.label}
                            {count > 0 && <span className="aop-tab-badge">{count}</span>}
                        </button>
                    );
                })}
            </div>

            {(actionError || successMessage) && (
                <div className={`aop-banner ${actionError ? 'aop-banner--error' : 'aop-banner--success'}`}>
                    {actionError || successMessage}
                </div>
            )}

            <div className="aop-bulk-actions">
                <span>{selectedCount} selected</span>
                <button className="aop-bulk-delete-btn" onClick={handleBulkDelete} disabled={selectedCount === 0}>
                    Delete Selected
                </button>
            </div>

            {/* Table */}
            <div className="aop-table-container">
                <table className="aop-table">
                    <thead>
                        <tr>
                            <th>
                                <label className="aop-checkbox-label">
                                    <input
                                        ref={selectAllCheckboxRef}
                                        type="checkbox"
                                        checked={areAllVisibleSelected}
                                        disabled={selectAllVisibleDisabled}
                                        onChange={toggleSelectAllVisible}
                                        aria-label="Select all visible orders"
                                    />
                                </label>
                            </th>
                            <th>Order ID</th>
                            <th>Customer</th>
                            <th>Items</th>
                            <th>Total</th>
                            <th>Date</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginated.length === 0 ? (
                            <tr><td colSpan={8} className="aop-empty">
                                {orders.length === 0 ? 'No orders yet.' : 'No orders match your search.'}
                            </td></tr>
                        ) : paginated.map(order => {
                            const sc         = STATUS_CONFIG[order.status] || { bg:'#eee', text:'#555', label:order.status||'—' };
                            const grandTotal = getOrderGrandTotal(order);
                            const hasDiscount = (order.discountAmount || 0) > 0;
                            const canSelect = isOrderSelectable(order);
                            return (
                                <tr key={order.firestoreId}>
                                    <td>
                                        <label className="aop-checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(selectedOrderIds[order.firestoreId])}
                                                disabled={!canSelect}
                                                onChange={() => toggleOrderSelection(order.firestoreId)}
                                                aria-label={`Select order ${order.orderId}`}
                                            />
                                        </label>
                                    </td>
                                    <td className="aop-mono">{order.orderId}</td>
                                    <td className="aop-customer-name">{order.customerName}</td>
                                    <td className="aop-center">{order.items}</td>
                                    <td>
                                        <div className="aop-total-cell">
                                            {grandTotal === 0
                                                ? <span className="aop-free-label">FREE</span>
                                                : `₱ ${grandTotal.toLocaleString()}`
                                            }
                                            {hasDiscount && (
                                                <span className="aop-discount-badge" title={
                                                    order.firstOrderDiscount
                                                        ? 'First Order Discount (15% off)'
                                                        : `Promo: ${order.promoCode || ''} (−₱${order.discountAmount?.toLocaleString()})`
                                                }>
                                                    {order.firstOrderDiscount ? '🎉 1st Order' : `−₱${order.discountAmount?.toLocaleString()}`}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>{formatDate(order.createdAt || order.date)}</td>
                                    <td>
                                        <span className="aop-status-badge" style={{ background:sc.bg, color:sc.text }}>
                                            {sc.label}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="aop-actions">
                                            <button className="aop-action-btn aop-view-btn" title="View Order"
                                                onClick={() => navigate('/admin/orders/details', { state:{ orderId:order.orderId, orderData:order } })}>
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                                                </svg>
                                            </button>
                                            {DELETABLE_STATUSES.includes(order.status) && (
                                                <button className="aop-action-btn aop-delete-btn"
                                                    title={order.status===ORDER_STATUS.CANCELLED||order.status===ORDER_STATUS.DELIVERED?'Move to Order Archive':'Cancel Order'}
                                                    onClick={() => setDeleteDialog({
                                                        firestoreId:   order.firestoreId,
                                                        orderId:       order.orderId,
                                                        status:        order.status,
                                                        userId:        order.userId,
                                                        customerName:  order.customerName,
                                                        customerEmail: order.customerEmail,
                                                        total:         order.total,
                                                    })}>
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                                                        <path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {bulkDeleteDialog && (
                <div className="aop-dialog-overlay" onClick={() => setBulkDeleteDialog(false)}>
                    <div className="aop-dialog" onClick={e => e.stopPropagation()}>
                        <h3 className="aop-dialog-title">Delete Selected Orders</h3>
                        <p className="aop-dialog-message">
                            {selectedCount === 1
                                ? `Delete the selected order? This action will cancel or archive the order depending on its current status.`
                                : `Delete the ${selectedCount} selected orders? This action will cancel or archive each selected order depending on its current status.`
                            }
                        </p>
                        <div className="aop-dialog-actions">
                            <button className="aop-dialog-btn aop-dialog-btn--secondary" onClick={() => setBulkDeleteDialog(false)}>Cancel</button>
                            <button className="aop-dialog-btn aop-dialog-btn--danger" onClick={handleConfirmBulkDelete}>Confirm</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="aop-pagination">
                <span className="aop-result-count">
                    Showing {filtered.length===0?0:(page-1)*entries+1}–{Math.min(page*entries,filtered.length)} of {filtered.length} orders
                </span>
                <button className="aop-page-btn" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Previous</button>
                <span className="aop-page-divider">|</span>
                <button className="aop-page-btn" disabled={page===totalPages} onClick={() => setPage(p=>p+1)}>Next →</button>
            </div>
        </div>
    );
}
